import { useState } from 'react'
import { useCart } from '../contexts/CartContext'
import { useLanguage } from '../contexts/LanguageContext'
import { ref, push } from 'firebase/database'
import { database } from '../config/firebase'
import Navbar from '../components/Navbar/Navbar'
import Footer from '../components/Footer/Footer'
import { ExtraService } from '../types'
import styles from './Cart.module.css'

const Cart = () => {
  const { t } = useLanguage()
  const { cartItems, removeFromCart, clearCart, getTotalPrice } = useCart()
  
  const extraServices: ExtraService[] = [
    {
      id: 'security',
      name: t('cart.service.security'),
      price: 250,
      period: 'monthly'
    },
    {
      id: 'service',
      name: t('cart.service.service'),
      price: 500,
      period: 'monthly'
    },
    {
      id: 'hosting',
      name: t('cart.service.hosting'),
      price: 250,
      period: 'monthly'
    }
  ]
  const [selectedServices, setSelectedServices] = useState<Set<string>>(new Set())
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const toggleService = (serviceId: string) => {
    setSelectedServices((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(serviceId)) {
        newSet.delete(serviceId)
      } else {
        newSet.add(serviceId)
      }
      return newSet
    })
  }

  const getExtraServicesTotal = () => {
    return extraServices
      .filter(service => selectedServices.has(service.id))
      .reduce((total, service) => total + service.price, 0)
  }

  const getGrandTotal = () => {
    return getTotalPrice() + getExtraServicesTotal()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (cartItems.length === 0) {
      alert(t('cart.alert.empty'))
      return
    }

    if (!formData.firstName || !formData.lastName || !formData.phone) {
      alert(t('cart.alert.fillFields'))
      return
    }

    setIsSubmitting(true)

    try {
      const selectedServicesList = extraServices.filter(service => selectedServices.has(service.id))
      const servicesText = selectedServicesList.length > 0
        ? `\n\n${t('cart.extraServices')}:\n${selectedServicesList.map(service => `${service.name} - ${service.price.toLocaleString('tr-TR')}₺${t('cart.monthly')}`).join('\n')}`
        : ''

      const orderData = {
        name: `${formData.firstName} ${formData.lastName}`,
        phone: formData.phone,
        service: cartItems.map((item) => item.name).join(', '),
        message: `${t('cart.orderSummary')}:\n${cartItems.map((item) => `${item.name} - ${item.price.toLocaleString('tr-TR')}₺`).join('\n')}${servicesText}\n\n${t('cart.subtotal')}: ${getTotalPrice().toLocaleString('tr-TR')}₺\n${t('cart.extraServices')}: ${getExtraServicesTotal().toLocaleString('tr-TR')}₺${t('cart.monthly')}\n${t('cart.total')}: ${getGrandTotal().toLocaleString('tr-TR')}₺`,
        timestamp: new Date().toISOString(),
        date: new Date().toLocaleString('tr-TR'),
        status: 'Aranmadı',
        teklifUcreti: getGrandTotal(),
        kazanilanRakam: 0
      }

      const contactsRef = ref(database, 'contacts')
      await push(contactsRef, orderData)

      setSubmitSuccess(true)
      clearCart()
      setSelectedServices(new Set())
      setFormData({ firstName: '', lastName: '', phone: '' })

      setTimeout(() => {
        setSubmitSuccess(false)
        window.location.href = '/'
      }, 3000)
    } catch (error) {
      console.error('Error submitting order:', error)
      alert(t('cart.alert.error'))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (cartItems.length === 0) {
    return (
      <>
        <Navbar />
        <section className={styles.cartSection}>
          <div className={styles.container}>
            <div className={styles.emptyCart}>
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
              <h2>{t('cart.empty')}</h2>
              <p>{t('cart.emptyDesc')}</p>
              <a 
                href="/#packages-preview" 
                className={styles.shopBtn}
                onClick={(e) => {
                  e.preventDefault()
                  window.location.href = '/'
                  setTimeout(() => {
                    const element = document.getElementById('packages-preview')
                    if (element) {
                      const targetPosition = element.offsetTop - 80
                      window.scrollTo({
                        top: targetPosition,
                        behavior: 'smooth'
                      })
                    }
                  }, 100)
                }}
              >
                {t('cart.browsePackages')}
              </a>
            </div>
          </div>
        </section>
        <Footer />
      </>
    )
  }

  return (
    <>
      <Navbar />
      <section className={styles.cartSection}>
        <div className={styles.container}>
          <h1 className={styles.title}>{t('cart.title')}</h1>

          <div className={styles.cartContent}>
            <div className={styles.cartItems}>
              {cartItems.map((item) => (
                <div key={item.id} className={styles.cartItem}>
                  <div className={styles.itemInfo}>
                    <h3 className={styles.itemName}>{item.name}</h3>
                  </div>
                  <div className={styles.itemActions}>
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className={styles.removeBtn}
                      title={t('cart.remove')}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}

              <div className={styles.extraServices}>
                <h2 className={styles.servicesTitle}>{t('cart.extraServices')}</h2>
                <div className={styles.servicesList}>
                  {extraServices.map((service) => (
                    <label key={service.id} className={styles.serviceItem}>
                      <input
                        type="checkbox"
                        checked={selectedServices.has(service.id)}
                        onChange={() => toggleService(service.id)}
                        className={styles.serviceCheckbox}
                      />
                      <div className={styles.serviceInfo}>
                        <span className={styles.serviceName}>{service.name}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className={styles.checkoutSection}>

              <div className={styles.orderSummary}>
                <h2 className={styles.summaryTitle}>{t('cart.orderSummary')}</h2>
              </div>

              <form onSubmit={handleSubmit} className={styles.checkoutForm}>
                <h2 className={styles.formTitle}>{t('cart.contactInfo')}</h2>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="firstName">{t('cart.firstName')} *</label>
                    <input
                      type="text"
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      required
                      placeholder={t('cart.firstNamePlaceholder')}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="lastName">{t('cart.lastName')} *</label>
                    <input
                      type="text"
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      required
                      placeholder={t('cart.lastNamePlaceholder')}
                    />
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="phone">{t('cart.phone')} *</label>
                  <input
                    type="tel"
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    placeholder={t('cart.phonePlaceholder')}
                  />
                </div>
                <button
                  type="submit"
                  className={styles.submitBtn}
                  disabled={isSubmitting || submitSuccess}
                >
                  {isSubmitting ? (
                    <>
                      <svg className={styles.spinner} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
                      </svg>
                      <span>{t('cart.submitting')}</span>
                    </>
                  ) : submitSuccess ? (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      <span>{t('cart.submitSuccess')}</span>
                    </>
                  ) : (
                    <>
                      <span>{t('cart.submit')}</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  )
}

export default Cart

